import React from "react";
import { Check, Lock } from "lucide-react";
import { cn } from "../../../lib/cn";

/**
 * StepIndicator — ui-v2.5. Progreso de un flujo por pasos. Especificación: catálogo · sección StepIndicator.
 * Tres variantes: `dots` (con etiquetas), `bars`, `list` (vertical).
 *
 * <StepIndicator variant="dots|bars|list" steps={["Personales","Dirección",…]} current={3} />
 *  - steps: array de strings (o { label }).  current: paso activo (1-based).
 *  - done = idx < current · active = idx === current · pending = idx > current.
 */

export function StepIndicator({ variant = "dots", steps = [], current = 1, className }) {
  const labels = steps.map((s) => (typeof s === "string" ? s : s.label));
  const n = labels.length;

  if (variant === "bars") {
    return (
      <div className={className}>
        <div className="flex gap-1.5">
          {labels.map((_, i) => (
            <div key={i} className={cn("h-1 flex-1 rounded-v25-full", i < current ? "bg-v25-brand-600" : "bg-v25-line")} />
          ))}
        </div>
        <p className="mt-2 text-[11px] text-v25-ink-500">
          Paso {current} de {n}{labels[current - 1] ? ` · ${labels[current - 1]}` : ""}
        </p>
      </div>
    );
  }

  if (variant === "list") {
    return (
      <div className={cn("max-w-xs space-y-0.5", className)}>
        {labels.map((label, i) => {
          const idx = i + 1;
          const done = idx < current;
          const active = idx === current;
          return (
            <div
              key={i}
              className={cn(
                "flex items-center gap-2.5 rounded-v25-md px-2 py-2",
                done ? "bg-v25-surface-raised" : active ? "bg-[rgba(0,102,179,0.08)]" : ""
              )}
            >
              <span
                className={cn(
                  "grid h-5 w-5 shrink-0 place-items-center rounded-v25-full text-[11px] font-bold",
                  done ? "bg-v25-active-bg text-v25-active-fg" : active ? "bg-v25-brand-600 text-white" : "bg-v25-line text-v25-ink-400"
                )}
              >
                {done ? <Check className="h-3 w-3" /> : idx}
              </span>
              <span className={cn("flex-1 text-sm", active ? "font-medium text-v25-brand-800" : done ? "text-v25-ink-900" : "text-v25-ink-500")}>
                {label}
              </span>
              {idx > current && <Lock className="h-3.5 w-3.5 text-v25-ink-300" />}
            </div>
          );
        })}
      </div>
    );
  }

  // dots (por defecto)
  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        {labels.map((_, i) => {
          const idx = i + 1;
          const done = idx < current;
          const active = idx === current;
          return (
            <React.Fragment key={i}>
              {i > 0 && <div className={cn("h-px flex-1", idx <= current ? "bg-v25-line-strong" : "bg-v25-line")} />}
              <div
                className={cn(
                  "grid h-6 w-6 shrink-0 place-items-center rounded-v25-full text-[11px] font-semibold",
                  done ? "bg-v25-brand-800 text-white" : active ? "bg-v25-brand-600 text-white" : "bg-v25-line text-v25-ink-400"
                )}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : idx}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-v25-ink-500">
        {labels.map((label, i) => (
          <span key={i} className={i + 1 === current ? "font-medium text-v25-ink-700" : ""}>{label}</span>
        ))}
      </div>
    </div>
  );
}
